import React, { useMemo } from 'react';
import { ActionIcon, Badge, Paper, Text, Tooltip } from '@mantine/core';
import { IconRotateClockwise } from '@tabler/icons-react';
import {
    DataTable,
    DataTableColumn,
    DataTableRowExpansionProps,
} from 'mantine-datatable';
import dayjs from 'dayjs';

import { When } from '../common';
import { PriceSyncQueueFilters } from './PriceSyncQueueFilters';
import { PriceSyncRowExpansionContent } from './PriceSyncRowDetail';
import type { QueueRow } from './priceSync.types';
import { getStatusColor, PAGE_SIZE, trimLeadingZeros } from './priceSync.utils';

type PriceSyncQueueTableProps = {
    rows: QueueRow[];
    total: number;
    page: number;
    rowsLoading: boolean;
    retryLoading: boolean;
    expandedRow: string | null;
    statusFilter: string;
    storeFilter: string;
    skuFilter: string;
    onPageChange: (page: number) => void;
    onExpandedRowChange: (id: string | null) => void;
    onRetry: (queueId: string) => void;
    onStatusChange: (value: string) => void;
    onStoreChange: (value: string) => void;
    onSkuChange: (value: string) => void;
};

export function PriceSyncQueueTable({
    rows,
    total,
    page,
    rowsLoading,
    retryLoading,
    expandedRow,
    statusFilter,
    storeFilter,
    skuFilter,
    onPageChange,
    onExpandedRowChange,
    onRetry,
    onStatusChange,
    onStoreChange,
    onSkuChange,
}: PriceSyncQueueTableProps) {
    const rowExpansion = useMemo(
        (): DataTableRowExpansionProps<QueueRow> => ({
            allowMultiple: false,
            expanded: {
                recordIds: expandedRow ? [expandedRow] : [],
                onRecordIdsChange: ids =>
                    onExpandedRowChange((ids[0] as string) ?? null),
            },
            content: PriceSyncRowExpansionContent,
        }),
        [expandedRow, onExpandedRowChange],
    );

    const columns = useMemo(
        (): DataTableColumn<QueueRow>[] => [
            {
                accessor: 'storeNo',
                title: 'Store',
                width: 90,
                noWrap: true,
            },
            {
                accessor: 'sku',
                title: 'SKU',
                width: 150,
                ellipsis: true,
                render: (r: QueueRow) => trimLeadingZeros(r.sku),
            },
            {
                accessor: 'status',
                title: 'Status',
                width: 100,
                render: (r: QueueRow) => (
                    <Badge
                        size="sm"
                        variant="light"
                        color={getStatusColor(r.status)}
                    >
                        {r.status}
                    </Badge>
                ),
            },
            {
                accessor: 'attempts',
                title: 'Attempts',
                width: 90,
                textAlign: 'center' as const,
            },
            {
                accessor: 'lastTriggeredAt',
                title: 'Last triggered',
                width: 140,
                noWrap: true,
                render: (r: QueueRow) =>
                    dayjs(r.lastTriggeredAt).format('DD MMM HH:mm'),
            },
            {
                accessor: 'sentAt',
                title: 'Sent at',
                width: 140,
                noWrap: true,
                render: (r: QueueRow) =>
                    r.sentAt ? dayjs(r.sentAt).format('DD MMM HH:mm') : '—',
            },
            {
                accessor: 'lastError',
                title: 'Last error',
                ellipsis: true,
                render: (r: QueueRow) => (
                    <When
                        condition={Boolean(r.lastError)}
                        fallback={
                            <Text size="xs" c="dimmed">
                                —
                            </Text>
                        }
                    >
                        <Text size="xs" c="red" lineClamp={1}>
                            {r.lastError}
                        </Text>
                    </When>
                ),
            },
            {
                accessor: 'actions',
                title: '',
                width: 60,
                render: (r: QueueRow) => (
                    <Tooltip label="Retry" withArrow>
                        <ActionIcon
                            variant="light"
                            color="orange"
                            size="md"
                            loading={retryLoading}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onRetry(r.id);
                            }}
                        >
                            <IconRotateClockwise size={16} />
                        </ActionIcon>
                    </Tooltip>
                ),
            },
        ],
        [retryLoading, onRetry],
    );

    return (
        <Paper withBorder radius="lg" p="md">
            <PriceSyncQueueFilters
                statusFilter={statusFilter}
                storeFilter={storeFilter}
                skuFilter={skuFilter}
                onStatusChange={onStatusChange}
                onStoreChange={onStoreChange}
                onSkuChange={onSkuChange}
            />
            <DataTable
                withTableBorder
                striped
                highlightOnHover
                records={rows}
                fetching={rowsLoading}
                totalRecords={total}
                recordsPerPage={PAGE_SIZE}
                page={page}
                onPageChange={onPageChange}
                rowExpansion={rowExpansion}
                minHeight={rows.length === 0 ? 160 : undefined}
                noRecordsText="No queue rows match the current filters."
                columns={columns}
            />
        </Paper>
    );
}
